import { useNavigate } from 'react-router'

import { formatDate } from '@/components/documents/format'
import { ResponsiveTable, type ResponsiveTableColumn } from '@/components/ui/responsive-table'
import { SkeletonRows } from '@/components/ui/skeleton'
import { type ComplianceSlot, useComplianceFile } from '@/models/vault'
import { SlotStatusPill } from './SlotStatusPill'
import { useVault } from './VaultContext'
import { VaultSection } from './VaultSection'

// Same trailing tracks as the driver and vehicle tables, so Status lines up down the page.
const COLUMNS: ResponsiveTableColumn<ComplianceSlot>[] = [
  {
    key: 'label',
    header: 'Document',
    cell: (slot) => <span className="truncate font-medium">{slot.label}</span>,
    hideOnCard: true,
  },
  {
    key: 'expires',
    header: 'Expires',
    cell: (slot) => (slot.dateExpiration ? formatDate(slot.dateExpiration) : null),
    headClassName: 'w-44',
    cellClassName: 'text-muted-foreground w-44 whitespace-nowrap',
  },
  {
    key: 'status',
    header: 'Status',
    cell: (slot) => <SlotStatusPill status={slot.status} />,
    headClassName: 'w-32',
    cellClassName: 'w-32',
  },
]

/** The company's own file, listed where the Business folder used to be. */
export function VaultCarrierTable({ title }: { title: string }) {
  const { orgId, href } = useVault()
  const navigate = useNavigate()
  const { file, isLoading } = useComplianceFile(orgId, 'carrier')
  const slots = file ? file.sections.flatMap((section) => section.slots) : []

  return (
    <VaultSection title={title} count={slots.length || undefined} scope="carrier">
      {isLoading || !file ? (
        <SkeletonRows />
      ) : (
        <ResponsiveTable
          items={slots}
          columns={COLUMNS}
          getRowId={(slot) => slot.key}
          cardTitle={(slot) => slot.label}
          onRowClick={() => void navigate(href('carrier'), { preventScrollReset: true })}
        />
      )}
    </VaultSection>
  )
}
